import { keepPreviousData } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { AgentPanel, conversationKeys, usePointTo } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import { useCurrentMe } from "../../../app/auth";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Counter } from "../../../ui/Counter";
import { EmptyState, Loading } from "../../../ui/EmptyState";
import { HandOverList } from "./HandOverList";
import { InboxFilters } from "./InboxFilters";
import { InboxGroupView } from "./InboxGroupView";
import { useInboxFilters } from "./useInboxFilters";

/** How long the entry the agent pointed at stays marked. */
const POINT_MARK_MS = 10_000;

/**
 * The SME's inbox (ui-direction screen 4, architecture 12.5): everything that waits for the SME, grouped by item with
 * the family breadcrumb, Unsure first, homes first. A hand-over only adds marks; the SME may open any waiting element.
 */
export function InboxPage() {
  const me = useCurrentMe();
  const filters = useInboxFilters();
  // While a new filter is read, the list and the filters stay on the screen, so focus is not lost.
  const query = useApiQuery("inbox", filters.values, { placeholderData: keepPreviousData });
  const [pointedId, setPointedId] = useState<string | null>(null);
  const [helperOpen, setHelperOpen] = useState(false);

  usePointTo(
    (event) => {
      if (event.element_id) setPointedId(event.element_id);
    },
    { screens: ["sme_inbox"] },
  );
  useEffect(() => {
    if (!pointedId) return;
    const row = Array.from(document.querySelectorAll<HTMLElement>("[data-element]")).find((node) => node.dataset.element === pointedId);
    row?.scrollIntoView?.({ block: "center" });
    const timer = setTimeout(() => setPointedId(null), POINT_MARK_MS);
    return () => clearTimeout(timer);
  }, [pointedId]);

  const inbox = query.data;
  const membershipId = me.active?.membership_id;

  return (
    <div className="ra-page stack">
      <header className="ra-page__head">
        <h1>SME inbox</h1>
        <p className="muted">
          Everything that waits for you, grouped by item. You decide card by card: Validate, Send back with a comment, Reject with a reason, or add a Note.
        </p>
      </header>

      {query.isPending && <Loading>Reading the inbox</Loading>}
      {query.isError && (
        <Card role="alert">
          <p className="form-error">{refusalText(query.error)}</p>
          <Button onClick={() => void query.refetch()}>Try again</Button>
        </Card>
      )}

      {inbox && (
        <>
          <Card className="ra-counts" aria-label="What is waiting">
            <div className="row">
              <Counter label="waiting for you" value={inbox.counts.waiting} tone="waiting" />
              <Counter label="Unsure" value={inbox.counts.unsure} />
              <Counter label="blocked" value={inbox.counts.blocked} />
            </div>
          </Card>

          <HandOverList inbox={inbox} onlyEngineer={filters.values.engineer} onPickEngineer={(value) => filters.set("engineer", value)} />

          <InboxFilters options={inbox.filters} values={filters.values} onChange={filters.set} onClear={filters.clear} active={filters.active} />

          {inbox.groups.length === 0 ? (
            filters.active ? (
              <EmptyState
                title="Nothing matches these filters"
                action={
                  <Button onClick={filters.clear} variant="primary">
                    Clear the filters
                  </Button>
                }
              >
                Elements that wait for you are hidden by the filters above.
              </EmptyState>
            ) : (
              <EmptyState title="Nothing is waiting for you">
                When an engineer accepts a card, adds an element by hand or sends a hand-over, it appears here. You can open any waiting element, with or without a hand-over.
              </EmptyState>
            )
          ) : (
            <div className="stack" aria-label="Waiting elements, by item">
              {inbox.groups.map((group, index) => (
                <InboxGroupView key={group.item?.element_id ?? `none-${index}`} group={group} pointedId={pointedId} />
              ))}
            </div>
          )}
        </>
      )}

      {membershipId && (
        <section className="ra-helper" aria-label="Review helper">
          {helperOpen ? (
            <>
              <AgentPanel
                conversationKey={conversationKeys.sme(membershipId)}
                mode="sme_review"
                context={{ screen: "sme_inbox" }}
                title="Review helper"
                emptyHint="Ask what a word means, or open a card and use the questions above it."
              />
              <Button variant="quiet" onClick={() => setHelperOpen(false)}>
                Hide the review helper
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setHelperOpen(true)}>
              Ask the review helper
            </Button>
          )}
        </section>
      )}
    </div>
  );
}
